/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { SearchExpand, SideNav, SideNavSection, SideNavTitle } from '@repo/shared-client/components/blocks'
import {
  InputFormField,
  SliderFormField,
  SwitchFormField,
  TagMultiSelectFormField,
  tagMultiSelectFromTags,
} from '@repo/shared-client/components/fields'
import { Button } from '@repo/shared-client/components/ui'
import { Form, useFormContext } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import SFJoinButton from '@web/src/components/primatives/sf-join-button/sf-join-button'
import { usePlaceCategoryOptions } from '@web/src/features/place/use-place-category-options'
import { useTranslation } from '@web/src/lib/intl/react'
import {
  FolderOpenIcon,
  FunnelIcon,
  FunnelPlusIcon,
  MapIcon,
  MapPinIcon,
  RotateCcwIcon,
  SearchIcon,
  TagIcon,
} from 'lucide-react'
import { useCallback } from 'react'
import SFInfoPopup from 'src/components/primatives/sf-info-popup/sf-info-popup'
import SFLocationSearchField from 'src/components/primatives/sf-location-search-field/sf-location-search-field'
import {
  PLACE_LIST_DISTANCE_UNLIMITED_FOR_APPLY,
  PLACE_LIST_LOCATION_EMPTY_FOR_APPLY,
  type PlaceListFilterFormValues,
} from '../place-list-filter-types'
import { usePlaceListFilterFields, usePlaceListFilterForm } from '../use-place-list-filter-form'

export interface PlaceListSideNavProps {
  tags: Array<{ id: string; name: string }>
  isAuthenticated: boolean
  hasPermission: (permission: string) => boolean
  defaultLocation?: string
  filterDefaults: PlaceListFilterFormValues
  /** Form values after clearing search (profile-backed location when URL has none). */
  filterResetValues: PlaceListFilterFormValues
  onFiltersApply: (values: PlaceListFilterFormValues) => void
  onReset: () => void
  /**
   * When false, hides the distance radius ("distance within") control and does not send a distance
   * radius in applied filter results (URL/search treats it as unlimited / no radius filter).
   */
  distanceWithinEnabled?: boolean
  /**
   * When false, hides the starting location ("distance from") control and does not send location
   * in applied filter results (clears `location` / `locationLat` / `locationLong` in list URL handlers).
   */
  distanceFromEnabled?: boolean
}

function PlaceListSideNavFields({
  tags,
  isAuthenticated,
  hasPermission,
  defaultLocation,
  filterResetValues,
  onFiltersApply,
  onReset,
  distanceWithinEnabled = true,
  distanceFromEnabled = true,
}: Omit<PlaceListSideNavProps, 'filterDefaults'>) {
  const { t } = useTranslation()
  const { reset } = useFormContext<PlaceListFilterFormValues>()
  const onFiltersApplyResolved = useCallback(
    (values: PlaceListFilterFormValues) => {
      let next = values
      if (!distanceWithinEnabled) {
        next = { ...next, distance: PLACE_LIST_DISTANCE_UNLIMITED_FOR_APPLY }
      }
      if (!distanceFromEnabled) {
        next = { ...next, location: PLACE_LIST_LOCATION_EMPTY_FOR_APPLY }
      }
      onFiltersApply(next)
    },
    [distanceFromEnabled, distanceWithinEnabled, onFiltersApply]
  )
  const { triggerFilterChange, distance, handleSearchChange } = usePlaceListFilterFields(onFiltersApplyResolved)

  const { selectOptions: categoryOptions } = usePlaceCategoryOptions()

  const handleResetFilters = useCallback(() => {
    reset(filterResetValues)
    onReset()
  }, [filterResetValues, onReset, reset])

  const filtersDisabled = !isAuthenticated

  return (
    <SideNav
      className="h-full min-h-0 flex-1"
      collapseAriaLabel={t('Collapse filters')}
      collapsedTitleIcon={<FunnelIcon />}
      expandAriaLabel={t('Expand filters')}
      header={<SideNavTitle description={t('Refine your search')} title={t('Filters')} />}
      togglePosition="top"
    >
      <SideNavSection className="pt-3" icon={<SearchIcon />} label={t('Search')}>
        <SearchExpand
          clearSearchLabel={t('Clear search')}
          debounceTime={300}
          filterControlsDisabled={filtersDisabled}
          inputProps={{ 'aria-label': t('Search places') }}
          minChars={0}
          name="name"
          onSearchChange={handleSearchChange}
          placeholder={t('Search by name...')}
          toggleFiltersLabel={t('Toggle filters')}
        />
      </SideNavSection>

      {distanceWithinEnabled && (
        <SideNavSection className="pt-3" icon={<MapPinIcon />} label={t('Distance Within')}>
          <div className="flex w-full items-center justify-between gap-2">
            <SliderFormField
              disabled={filtersDisabled}
              name="distance"
              onValueChangeEnd={() => triggerFilterChange()}
              sliderProps={{
                className: 'flex-1 min-w-0 px-3 [&_[data-slot=slider-track]]:bg-card/80',
                max: 1000,
                min: 10,
              }}
            />
            <span className="min-w-[50px] whitespace-nowrap text-right">
              {distance?.[0] < 1000 ? t('{{value}} km', { value: distance[0] }) : t('ALL')}
            </span>
          </div>
        </SideNavSection>
      )}

      {distanceFromEnabled && (
        <SideNavSection className="pt-3" icon={<MapIcon />} label={t('Distance From')}>
          {hasPermission('location:change-location') ? (
            <div className={cn('w-full', filtersDisabled && 'pointer-events-none opacity-60')}>
              <SFLocationSearchField
                name="location"
                onLocationChange={() => triggerFilterChange()}
                placeholder={t('Enter starting location')}
              />
            </div>
          ) : (
            <div className="flex w-full items-center justify-center">
              <InputFormField
                disabled
                inputProps={{
                  className: 'bg-muted/50',
                  readOnly: true,
                  value: isAuthenticated ? defaultLocation : t('Guelph, Ontario, Canada'),
                }}
                name="locationPlaceholder"
              />
              <SFInfoPopup
                description={
                  isAuthenticated
                    ? t('Adjusting this is available to supporters only')
                    : t('You must be member to adjust the starting location, join for free.')
                }
                iconButtonProps={{ size: 'icon-lg' }}
                title={isAuthenticated ? t('Supporters Only') : t('Members Only')}
              />
            </div>
          )}
        </SideNavSection>
      )}

      <SideNavSection className="pt-3" icon={<FolderOpenIcon />} label={t('Category')}>
        <TagMultiSelectFormField
          disabled={filtersDisabled}
          name="categories"
          onValueChange={() => triggerFilterChange()}
          onValueChangeTrigger="close"
          options={categoryOptions}
          placeholder={t('All')}
        />
      </SideNavSection>

      <SideNavSection className="pt-3" icon={<TagIcon />} label={t('Tag Filter')}>
        <TagMultiSelectFormField
          disabled={filtersDisabled}
          name="tags"
          onValueChange={() => triggerFilterChange()}
          onValueChangeTrigger="close"
          options={tagMultiSelectFromTags(tags)}
          placeholder={t('All')}
        />
      </SideNavSection>

      <SideNavSection className="pt-3" icon={<FunnelPlusIcon />} label={t('Other Filters')}>
        <div className="flex flex-col gap-3 py-1">
          {isAuthenticated && (
            <SwitchFormField
              disabled={filtersDisabled}
              label={t('Places I Maintain')}
              name="myPlaces"
              onCheckedChange={(checked) => triggerFilterChange({ myPlaces: checked })}
            />
          )}

          {hasPermission('place:review') && (
            <SwitchFormField
              disabled={filtersDisabled}
              label={t('Needs Review')}
              name="needsReview"
              onCheckedChange={(checked) => triggerFilterChange({ needsReview: checked })}
            />
          )}
        </div>
      </SideNavSection>

      {isAuthenticated ? (
        <SideNavSection hideWhenCollapsed>
          <Button
            className="mt-4 w-full text-muted-foreground"
            onClick={handleResetFilters}
            size="sm"
            type="button"
            variant="ghost"
          >
            <RotateCcwIcon size={14} />
            {t('Reset Filters')}
          </Button>
        </SideNavSection>
      ) : (
        <SideNavSection hideWhenCollapsed>
          <SFJoinButton className="mt-4 w-full" size="sm" />
        </SideNavSection>
      )}
    </SideNav>
  )
}

export default function PlaceListSideNav({
  tags,
  isAuthenticated,
  hasPermission,
  defaultLocation,
  filterDefaults,
  filterResetValues,
  onFiltersApply,
  onReset,
  distanceWithinEnabled = true,
  distanceFromEnabled = true,
}: PlaceListSideNavProps) {
  const formMethods = usePlaceListFilterForm(filterDefaults)

  return (
    <Form className="flex h-full min-h-0 w-fit flex-col" formMethods={formMethods} suppressHydrationWarning>
      <PlaceListSideNavFields
        defaultLocation={defaultLocation}
        distanceFromEnabled={distanceFromEnabled}
        distanceWithinEnabled={distanceWithinEnabled}
        filterResetValues={filterResetValues}
        hasPermission={hasPermission}
        isAuthenticated={isAuthenticated}
        onFiltersApply={onFiltersApply}
        onReset={onReset}
        tags={tags}
      />
    </Form>
  )
}
